import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ruRU, enUS } from '@mui/material/locale';
import { useTranslation } from 'react-i18next';

const ThemeContext = createContext();
export const useThemeMode = () => useContext(ThemeContext);
const initialMode = () => { try { return localStorage.getItem('themeMode') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; } };

export function createWorkspaceTheme(mode, language = 'ru') {
  const dark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: { main: dark ? '#9abaff' : '#285ed7', contrastText: dark ? '#14243d' : '#ffffff' },
      secondary: { main: dark ? '#b4baf7' : '#6066a4' },
      success: { main: dark ? '#83d5b7' : '#17765f' },
      warning: { main: dark ? '#edc27d' : '#94621b' },
      error: { main: dark ? '#f0a4ae' : '#af4050' },
      info: { main: dark ? '#91c9eb' : '#33769b' },
      background: { default: dark ? '#101825' : '#f4f6fa', paper: dark ? '#182333' : '#ffffff' },
      text: { primary: dark ? '#edf3fc' : '#192a40', secondary: dark ? '#adbed4' : '#60728a' },
      divider: dark ? '#304056' : '#e2e8f0',
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
      fontSize: 14,
      h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-.8px' },
      h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-.6px' },
      h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-.5px' },
      h4: { fontSize: '1.65rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-.6px' },
      h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: '.9375rem', lineHeight: 1.65 },
      body2: { fontSize: '.8125rem', lineHeight: 1.6 },
      subtitle1: { fontSize: '.9375rem', fontWeight: 500 },
      subtitle2: { fontSize: '.8125rem', fontWeight: 600 },
      caption: { fontSize: '.75rem', lineHeight: 1.5 },
      button: { fontSize: '.8125rem', fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiCssBaseline: { styleOverrides: { body: { fontVariantNumeric: 'tabular-nums' }, '::selection': { background: dark ? '#314b79' : '#dce7ff' } } },
      MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 10, minHeight: 38, padding: '8px 14px', textTransform: 'none', '@media (pointer: coarse)': { minHeight: 44 } }, outlined: { borderColor: dark ? '#40516a' : '#dce4ef' } } },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10, '@media (pointer: coarse)': { minWidth: 44, minHeight: 44 } } } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: { styleOverrides: { root: { border: `1px solid ${dark ? '#304056' : '#e2e8f0'}`, boxShadow: 'none', borderRadius: 14 } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 }, input: { '@media (pointer: coarse)': { fontSize: 16 } } } },
      MuiChip: { styleOverrides: { root: { borderRadius: 7, fontWeight: 500, fontSize: 12 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16, backgroundImage: 'none', boxShadow: dark ? '0 20px 80px #0006' : '0 20px 80px #192a4026' } } },
      MuiTableCell: { styleOverrides: { root: { borderColor: dark ? '#304056' : '#e2e8f0', fontSize: 13, padding: '14px 18px' }, head: { color: dark ? '#adbed4' : '#60728a', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' } } },
      MuiTabs: { styleOverrides: { root: { minHeight: 44 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', minHeight: 44, fontSize: 13 } } },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiLink: { defaultProps: { underline: 'hover' } },
    },
  }, language.startsWith('ru') ? ruRU : language.startsWith('mn') ? { components: {
    MuiTablePagination: { defaultProps: { labelRowsPerPage: 'Хуудсан дахь мөр:', labelDisplayedRows: ({ from, to, count }) => `${from}–${to} / ${count === -1 ? to + '+' : count}`, getItemAriaLabel: type => ({ first: 'Эхний хуудас', last: 'Сүүлийн хуудас', next: 'Дараагийн хуудас', previous: 'Өмнөх хуудас' })[type] } },
    MuiAutocomplete: { defaultProps: { clearText: 'Арилгах', closeText: 'Хаах', openText: 'Нээх', loadingText: 'Ачаалж байна…', noOptionsText: 'Олдсонгүй' } },
    MuiAlert: { defaultProps: { closeText: 'Хаах' } },
    MuiPagination: { defaultProps: { getItemAriaLabel: (type, page, selected) => type === 'page' ? `${page}${selected ? ', сонгосон хуудас' : '-р хуудас'}` : ({ first: 'Эхний хуудас', last: 'Сүүлийн хуудас', next: 'Дараагийн хуудас', previous: 'Өмнөх хуудас' })[type] } },
  } } : enUS);
}

export default function ThemeProvider({ children }) {
  const { i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || 'ru';
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark');
    document.documentElement.style.colorScheme = mode;
    try { localStorage.setItem('themeMode', mode); } catch {}
  }, [mode]);
  const theme = useMemo(() => createWorkspaceTheme(mode, language), [mode, language]);
  return <ThemeContext.Provider value={{ mode, toggleMode: () => setMode(value => value === 'light' ? 'dark' : 'light') }}>
    <MuiThemeProvider theme={theme}><CssBaseline />{children}</MuiThemeProvider>
  </ThemeContext.Provider>;
}
